<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type {
  HistoryWindow,
  ServerHistory,
  ServerProcesses,
  ServerResources,
} from '@atum-remote/protocol/server-inspection';
import { bytes, rootStorage } from '../lib/server-inspection.js';
import {
  diskRates,
  duration,
  filesystemPercent,
  healthFindings,
  interfaceRates,
  iowaitPercent,
  isHostTraffic,
  linkSpeed,
  memoryBreakdown,
  perCoreUsage,
  rate,
  slowLatency,
  swapRate,
} from '../lib/server-resources.js';
import AppIcon from './AppIcon.vue';
import ResourceHistory from './ResourceHistory.vue';
import ResourceNetwork from './ResourceNetwork.vue';
import ResourceProcesses from './ResourceProcesses.vue';
import ResourceStorage from './ResourceStorage.vue';
import UsageBar from './UsageBar.vue';

/**
 * Current readings answer "is it okay", the sections answer "why", history answers "what
 * changed". Host-specific capabilities (for example a future Nyx Compute section) render
 * through the `capabilities` slot when the host lists them in `resources.capabilities`,
 * keeping this generic view free of workload-specific fields.
 */
const props = defineProps<{
  resources: ServerResources;
  previous: ServerResources | undefined;
  processes: ServerProcesses | undefined;
  history: ServerHistory | undefined;
  /** The 15-minute ring used for findings, independent of the charted window. */
  recentHistory: ServerHistory | undefined;
  historyWindow: HistoryWindow;
  cpu: number | null;
  /** CPU readings taken while this page is open, for hosts without recorded history. */
  sessionCpu: readonly number[];
  busy: boolean;
}>();
const emit = defineEmits<{ 'update:historyWindow': [window: HistoryWindow] }>();

const historySupported = computed(() => !!props.resources.capabilities?.includes('history'));
const cores = computed(() => props.resources.cpu?.cores ?? 0);
const memory = computed(() => memoryBreakdown(props.resources.memory));
const disks = computed(() => diskRates(props.previous, props.resources));
const links = computed(() => interfaceRates(props.previous, props.resources));
const perCore = computed(() => perCoreUsage(props.previous, props.resources));
const iowait = computed(() => iowaitPercent(props.previous, props.resources));
const swapping = computed(() => (swapRate(props.previous, props.resources) ?? 0) >= 256);

const root = computed(() => {
  const fs = props.resources.filesystems?.find((f) => f.mount === '/');
  if (fs) return { ...fs, percent: filesystemPercent(fs) };
  const legacy = rootStorage(props.resources.storage.stdout);
  return legacy ? { ...legacy, percent: legacy.percent, mount: '/' } : null;
});
const crowded = computed(
  () =>
    (props.resources.filesystems ?? []).filter((f) => f.mount !== '/' && filesystemPercent(f) >= 85)
      .length,
);
const diskTotals = computed(() => {
  if (!disks.value.size) return null;
  let read = 0,
    write = 0;
  // md arrays repeat their members' I/O; count physical (leaf) devices only.
  for (const disk of props.resources.disks ?? []) {
    const r = disks.value.get(disk.name);
    if (!r || disk.kind === 'raid') continue;
    read += r.readBytes;
    write += r.writeBytes;
  }
  return { read, write };
});
const traffic = computed(() => {
  const hosts = props.resources.network.filter((n) => isHostTraffic(n, props.resources.network));
  let received = 0,
    sent = 0,
    known = false;
  for (const item of hosts) {
    const r = links.value.get(item.name);
    if (!r) continue;
    known = true;
    received += r.received;
    sent += r.sent;
  }
  const main =
    hosts.find((n) => n.defaultRoute?.length) ?? hosts.find((n) => n.state === 'up') ?? hosts[0];
  return { received: known ? received : null, sent: known ? sent : null, main };
});

/** Latency and swapping must persist across two consecutive readings before they count. */
const prior = ref({ slow: new Set<string>(), swapping: false });
let current = { slow: new Set<string>(), swapping: false };
watch(
  () => props.resources,
  () => {
    prior.value = current;
    const slow = new Set<string>();
    for (const disk of props.resources.disks ?? []) {
      const r = disks.value.get(disk.name);
      if (
        r &&
        r.readIops + r.writeIops >= 10 &&
        Math.max(r.readLatency ?? 0, r.writeLatency ?? 0) >= slowLatency(disk)
      )
        slow.add(disk.name);
    }
    current = { slow, swapping: swapping.value };
  },
  { immediate: true },
);
const findings = computed(() =>
  healthFindings({
    resources: props.resources,
    previous: props.previous,
    history: props.recentHistory,
    slowDisks: prior.value.slow,
    swapping: prior.value.swapping,
  }),
);

const spark = computed(() =>
  props.sessionCpu
    .map((v, i) => `${(i * 100) / Math.max(1, props.sessionCpu.length - 1)},${30 - v * 0.3}`)
    .join(' '),
);
const coreColumns = computed(() => {
  const n = perCore.value?.length ?? cores.value;
  return n <= 16 ? n : n <= 64 ? 16 : 32;
});
const cells = computed(() =>
  (perCore.value ?? Array.from({ length: cores.value }, () => null)).map((value, i) => ({
    i,
    value,
    x: (i % coreColumns.value) * 12,
    y: Math.floor(i / coreColumns.value) * 12,
    // Opacity steps rather than hues keep the grid readable in both themes.
    opacity: value == null ? 0.08 : 0.14 + (0.86 * Math.min(100, value)) / 100,
  })),
);
const busyCores = computed(() => (perCore.value ?? []).filter((v) => (v ?? 0) >= 80).length);
const hottestCore = computed(() => Math.max(0, ...(perCore.value ?? []).map((v) => v ?? 0)));
const info = computed(() => props.resources.cpuInfo);
const scale = computed(() => {
  const parts: string[] = [];
  if (info.value?.sockets && info.value.sockets > 1) parts.push(`${info.value.sockets} sockets`);
  if (info.value?.physicalCores) parts.push(`${info.value.physicalCores} cores`);
  parts.push(`${cores.value} threads`);
  return parts.join(' · ');
});
</script>

<template>
  <section
    v-if="findings.length"
    class="panel res-findings"
    aria-labelledby="res-findings-title"
    :data-severity="findings.some((f) => f.severity === 'critical') ? 'critical' : 'warning'"
  >
    <h3 id="res-findings-title" class="sr-only">Needs attention</h3>
    <ul>
      <li v-for="f in findings" :key="f.id" :data-severity="f.severity">
        <AppIcon name="warning" />
        <span
          ><strong>{{ f.title }}</strong> <span>{{ f.detail }}</span></span
        >
      </li>
    </ul>
  </section>

  <div class="server-metrics res-metrics">
    <article class="panel server-metric">
      <h3>CPU</h3>
      <strong>{{
        !resources.cpu ? 'Unavailable' : cpu == null ? 'Sampling…' : `${cpu.toFixed(1)}%`
      }}</strong>
      <p>{{ resources.cpu ? scale : 'Unknown processor' }}</p>
      <p class="mono res-load" title="Load average over 1, 5 and 15 minutes">
        Load {{ resources.load.map((v) => v.toFixed(2)).join(' · ') || '—' }}
      </p>
      <svg
        v-if="!historySupported && sessionCpu.length > 1"
        class="res-spark"
        viewBox="0 0 100 32"
        preserveAspectRatio="none"
        role="img"
        aria-label="CPU usage over recent readings"
      >
        <polyline :points="spark" fill="none" stroke="currentColor" stroke-width="1.2" />
      </svg>
    </article>

    <article class="panel server-metric">
      <h3>Memory</h3>
      <strong>{{ memory ? `${memory.percent.toFixed(1)}%` : 'Unavailable' }}</strong>
      <template v-if="memory">
        <UsageBar
          class="res-metric-bar"
          :segments="[
            { value: memory.used, tone: memory.percent >= 90 ? 'warn' : 'ok' },
            { value: memory.cache, tone: 'soft' },
          ]"
          :total="memory.total"
          :label="`${bytes(memory.used)} in use, ${bytes(memory.cache)} cache, ${bytes(memory.free)} free`"
        />
        <p class="res-key">
          <span><span class="res-dot" data-tone="in" />{{ bytes(memory.used) }} in use</span>
          <span
            title="Page cache and reclaimable kernel memory. The kernel gives it back on demand."
            ><span class="res-dot" data-tone="soft" />{{ bytes(memory.cache) }} cache</span
          >
        </p>
        <p>{{ bytes(memory.available) }} available of {{ bytes(memory.total) }}</p>
        <p>
          Swap
          {{ memory.swapTotal ? `${bytes(memory.swapUsed)} of ${bytes(memory.swapTotal)}` : 'none'
          }}<span v-if="swapping" class="res-warn-text"> · actively swapping</span>
        </p>
      </template>
    </article>

    <article class="panel server-metric">
      <h3>Storage</h3>
      <strong>{{ root ? `${root.percent.toFixed(1)}%` : 'Unavailable' }}</strong>
      <template v-if="root">
        <UsageBar
          class="res-metric-bar"
          :segments="[
            {
              value: root.percent,
              tone: root.percent >= 95 ? 'danger' : root.percent >= 90 ? 'warn' : 'ok',
            },
          ]"
          :total="100"
          :label="`Root filesystem ${root.percent.toFixed(0)}% used`"
        />
        <p>{{ bytes(root.available) }} free of {{ bytes(root.total) }} on /</p>
      </template>
      <p v-if="diskTotals" class="res-key">
        <span><span class="res-dot" data-tone="in" />Read {{ rate(diskTotals.read) }}</span>
        <span><span class="res-dot" data-tone="out" />Write {{ rate(diskTotals.write) }}</span>
      </p>
      <p v-if="crowded" class="res-warn-text">
        {{ crowded }} other {{ crowded === 1 ? 'filesystem is' : 'filesystems are' }} over 85%
      </p>
    </article>

    <article class="panel server-metric">
      <h3>Network</h3>
      <strong class="res-rates">
        <span><span class="res-arrow" aria-hidden="true">↓</span>{{ rate(traffic.received) }}</span>
        <span><span class="res-arrow" aria-hidden="true">↑</span>{{ rate(traffic.sent) }}</span>
      </strong>
      <p v-if="traffic.main">
        <span class="mono">{{ traffic.main.name }}</span
        ><template v-if="linkSpeed(traffic.main.speedMbps)">
          · {{ linkSpeed(traffic.main.speedMbps) }}</template
        ><template v-if="traffic.main.state"> · {{ traffic.main.state }}</template>
      </p>
      <p>Up {{ duration(resources.uptime) }}</p>
    </article>
  </div>

  <ResourceHistory
    :history="history"
    :window="historyWindow"
    :supported="historySupported"
    :cores="cores"
    @update:window="emit('update:historyWindow', $event)"
  />

  <details v-if="resources.cpu" class="panel res-section res-details">
    <summary>
      <span class="res-summary-title">Processor</span>
      <span class="res-summary-meta">{{ info?.model ?? 'Unknown model' }} · {{ scale }}</span>
    </summary>
    <div class="res-cpu">
      <div>
        <svg
          class="res-cores"
          :viewBox="`0 0 ${coreColumns * 12} ${Math.ceil(cells.length / coreColumns) * 12}`"
          role="img"
          :aria-label="`Per-thread use: ${busyCores} of ${cells.length} above 80%, busiest ${hottestCore.toFixed(0)}%`"
        >
          <rect
            v-for="cell in cells"
            :key="cell.i"
            :x="cell.x"
            :y="cell.y"
            width="10"
            height="10"
            rx="2"
            :fill-opacity="cell.opacity"
          >
            <title>
              CPU {{ cell.i }} · {{ cell.value == null ? 'sampling' : `${cell.value.toFixed(0)}%` }}
            </title>
          </rect>
        </svg>
        <p class="res-faint res-scale">
          <span>Each square is one logical CPU; brighter is busier.</span>
          <span v-if="!perCore">Per-thread use appears after the next reading.</span>
        </p>
      </div>
      <dl class="res-facts">
        <div>
          <dt>Busiest thread</dt>
          <dd>{{ perCore ? `${hottestCore.toFixed(0)}%` : '—' }}</dd>
        </div>
        <div>
          <dt>Threads above 80%</dt>
          <dd>{{ perCore ? `${busyCores} of ${cells.length}` : '—' }}</dd>
        </div>
        <div>
          <dt>Waiting on I/O</dt>
          <dd>{{ iowait == null ? '—' : `${iowait.toFixed(1)}%` }}</dd>
        </div>
        <div>
          <dt>Load per CPU</dt>
          <dd class="mono">
            {{
              cores && resources.load.length
                ? resources.load.map((v) => (v / cores).toFixed(2)).join(' · ')
                : '—'
            }}
          </dd>
        </div>
        <div v-if="resources.pressure?.cpu">
          <dt>CPU stall (1 min)</dt>
          <dd>{{ resources.pressure.cpu.some.avg60.toFixed(1) }}%</dd>
        </div>
      </dl>
    </div>
  </details>

  <slot name="capabilities" :resources="resources" />

  <ResourceStorage :resources="resources" :rates="disks" />
  <ResourceNetwork :network="resources.network" :rates="links" />
  <ResourceProcesses :reading="processes" :busy="busy" />
</template>
